import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { useEnquire } from "@/components/ContactModal";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { setOpen } = useEnquire();
  const [menu, setMenu] = useState(false);

  const links = [
    { to: "/", label: t.nav.home, id: "nav-home-link" },
    { to: "/biography", label: t.nav.biography, id: "nav-biography-link" },
    { to: "/otto-cilindri-manuale", label: t.nav.vj8c, id: "nav-vj8c-link" },
    { to: "/bi-venti", label: t.nav.biventi, id: "nav-biventi-link" },
  ];

  return (
    <>
      <header data-testid="site-header" className="fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-[#0A0E14]/70 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-3">
          <span className="font-tech text-2xl font-medium uppercase tracking-[0.35em] text-white transition-colors duration-300 group-hover:text-[#4A7BA6] sm:text-3xl">Jano</span>
          <span className="font-tech text-2xl font-light uppercase tracking-[0.35em] text-white/55 sm:text-3xl">Automobili</span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.id}
              className={({ isActive }) =>
                `font-tech text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-[#4A7BA6]" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden items-center border border-white/15 sm:flex" data-testid="lang-toggle">
            {["en", "it"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}-toggle`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 font-tech text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  lang === l ? "bg-white text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-enquire-button"
            onClick={() => setOpen(true)}
            className="hidden rounded-full bg-[#4A7BA6] px-6 py-2.5 font-tech text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black md:block"
          >
            {t.nav.enquire}
          </button>
          <button
            data-testid="nav-menu-button"
            onClick={() => setMenu(true)}
            className="text-white md:hidden"
            aria-label="Menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>
      </header>

      <AnimatePresence>
        {menu && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-[#0A0E14] p-6 md:hidden"
          >
            <div className="flex items-center justify-between">
              <span className="font-tech text-base uppercase tracking-[0.3em]">Jano Automobili</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenu(false)} aria-label="Close">
                <X size={28} />
              </button>
            </div>
            <nav className="mt-20 flex flex-col gap-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-${l.id}`}
                    onClick={() => setMenu(false)}
                    className="font-heritage text-4xl italic text-white/90"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.button
                data-testid="mobile-enquire-button"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => { setMenu(false); setOpen(true); }}
                className="mt-8 w-fit rounded-full bg-[#4A7BA6] px-8 py-3 font-tech text-sm uppercase tracking-[0.2em]"
              >
                {t.nav.enquire}
              </motion.button>
              <div className="mt-6 flex items-center border border-white/15 w-fit">
                {["en", "it"].map((l) => (
                  <button
                    key={l}
                    data-testid={`mobile-lang-${l}-toggle`}
                    onClick={() => setLang(l)}
                    className={`px-4 py-2 font-tech text-sm uppercase tracking-[0.2em] ${lang === l ? "bg-white text-black" : "text-white/60"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
